import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import styled, { injectGlobal } from 'styled-components'
import { Formik } from 'formik'
import { DayPickerRangeController } from 'react-dates'
import moment from 'moment'
import to from 'await-catch'
import theme from '../../../styled/theme'
import { addFlashMessage, addFlashMessageSuccess, hideLoader, showLoader } from '../../../actions'
import { Section, SectionHeader, SectionTitle } from '../../../styled/sections'
import { Text2 } from '../../../components/ui/text'
import { Button } from '../../../components/ui/button'
import ResultsListDesk from '../../../components/resultsListDesk'
import ResultsList from '../../../components/resultCard'
import { useClinicsOptions } from '../../../hooks/useClinicsOptions'
import { useInstitutionOption } from '../../../hooks/useInstitutionOption'
import useIsMobile from '../../../hooks/useIsMobile'
import {
	cancelInvoiceSending,
	downloadInvoiceList,
	generateInvoicePackage,
	getDownloadLink,
	getInvoiceList,
	sendInvoice
} from '../../../api'
import Pagination from '../../../components/pagination'
import Input from '../../../components/ui/input'
import Select from '../../../components/ui/select'
import DualDateInput from '../../../components/DualDateInput'
import { FormFilter, PeriodoDiv, SelectInvoiceNumber } from './styled'
import { Text1 } from '../../../styled/generics'
import { Download, Mail, Out, Info, ArrowDouble } from '../../../assets/icons'
import { BILLING_GROUP, NOT_SENT, SENT } from '../../../constants'
import Autocomplete from '../../../components/ui/autoComplete'
import Modal from '../../../components/Modal'

injectGlobal`
  .CalendarDay__selected,
  .CalendarDay__selected:active,
  .CalendarDay__selected:hover {
    background: ${theme.colors.brand.blueOmint} !important;
    color: #fff !important;
    border: 1px solid ${theme.colors.brand.blueOmint} !important;
  }
.CalendarDay__selected_span{
background: ${theme.colors.greys.dividerGray} !important;
border: 1px solid #e4e7e7  !important;
	}
.CalendarDay__hovered_span{
background: ${theme.colors.greys.dividerGray} !important;
border: 1px solid #e4e7e7 !important;
	}

.DayPicker__withBorder {
	box-shadow: none !important;
	border-radius: 0 !important;
}
`

const formatInvoiceAmount = amount => {
	const parsedAmount = Number(amount)
	if (Number.isNaN(parsedAmount)) return '-'

	return parsedAmount.toLocaleString('es-AR', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	})
}

const parseInvoiceResults = (items = []) =>
	items.map((item, index) => ({
		...item,
		id: item.id || item.numeroComprobante || item.numeroFactura || index,
		factura: item.numeroFactura || '-',
		fecha: item.fecha || '-',
		importe: formatInvoiceAmount(item.importeComprobante),
		descargadaEstado: item.descargado ? 'Descargada' : 'No descargada',
		financiador: item.financiador || item.nombreFinanciador || item.obraSocial || '-'
	}))

const mapInvoicesToRequest = invoices => ({
	Facturaciones: invoices.map(invoice => ({
		centroFactura: invoice.centroFactura,
		numeroComprobante: invoice.numeroComprobante,
		tipoComprobante: invoice.tipoComprobante,
		tipoFactura: invoice.tipoFactura
	}))
})

function Invoice({ auth, showLoader, hideLoader, addFlashMessage, addFlashMessageSuccess }) {
	const [resultsLst, setResultsLst] = useState([])
	const [totalPages, setTotalPages] = useState(1)
	const [currentPage, setCurrentPage] = useState(1)
	const [submitting, setSubmitting] = useState(false)
	const [sort, setSort] = useState({ filtro: '', orden: '' })
	const [selectedInvoices, setSelectedInvoices] = useState([])
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		action: null,
		row: null,
		submitting: false
	})
	const userRoles = auth?.gruposRoles || []
	const isBillingUser = userRoles.includes(BILLING_GROUP)
	const isBillingMode = isBillingUser
	const [localFilters, setLocalFilters] = useState({
		clinic: '',
		tipoComprobante: '',
		tipoFactura: '',
		centroFactura: '',
		numeroComprobante: '',
		cuit: '',
		startDate: moment().subtract(115, 'days'),
		endDate: moment(),
		sentStatus: isBillingMode ? [SENT, NOT_SENT] : [SENT],
		institution: '',
		id: ''
	})

	const isMobile = useIsMobile()
	const clinicsOptionsObj = useClinicsOptions({ showLoader, hideLoader, addFlashMessage })
	const clinicsOptions = Array.isArray(clinicsOptionsObj.clinicsOptions)
		? clinicsOptionsObj.clinicsOptions
		: []

	const institutionObj = useInstitutionOption({ showLoader, hideLoader, addFlashMessage })
	const institutionOptions = Array.isArray(institutionObj.institutionOptions)
		? institutionObj.institutionOptions
		: []

	const billingDesktopFieldWidth = !isMobile && isBillingMode ? 'calc(33.33% - 11px)' : undefined

	const applyFiltersAndFetch = async ({ values, page = 1, sentStatusOverride, sortOverride }) => {
		setSubmitting(true)
		setResultsLst([])

		const selectedInstitution = institutionObj.institutions.find(
			institution => String(institution.id) === String(values.institution || values.id || '')
		)

		const cuit =
			selectedInstitution?.cuit ||
			auth?.metadatos?.find(metadata => metadata.metadato.clave === 'CUIT')?.valor ||
			''
		const formatDate = date => (date && date.toISOString ? date.toISOString() : null)

		const nextFilters = {
			clinic: values.clinic,
			tipoComprobante: values.tipoComprobante,
			tipoFactura: values.tipoFactura,
			centroFactura: values.centroFactura,
			numeroComprobante: values.numeroComprobante,
			cuit,
			institution: values.institution || '',
			startDate: values.startDate,
			endDate: values.endDate,
			sentStatus: sentStatusOverride || values.sentStatus || localFilters.sentStatus,
			id: selectedInstitution?.id || values.id || ''
		}

		const nextSort = sortOverride || sort

		const params = {
			cuit,
			idClinica: values.clinic,
			tipoComprobante: values.tipoComprobante,
			tipoFactura: values.tipoFactura,
			centroFactura: values.centroFactura,
			numeroComprobante: values.numeroComprobante,
			fechaDesde: formatDate(values.startDate),
			fechaHasta: formatDate(values.endDate),
			pagina: page,
			itemsPorPagina: 10,
			id: selectedInstitution?.id || values.id || '',
			estadoEnvio: nextFilters.sentStatus,
			...(nextSort?.filtro && nextSort?.orden
				? { filtro: nextSort.filtro, orden: nextSort.orden }
				: {})
		}

		try {
			const [err, { data } = {}] = await to(getInvoiceList(params))
			if (!err && data && Array.isArray(data.items)) {
				setResultsLst(parseInvoiceResults(data.items))
				setTotalPages(data.totalPaginas || 1)
			} else {
				setResultsLst([])
				setTotalPages(1)
			}
			setLocalFilters(nextFilters)
			setCurrentPage(page)
			setSelectedInvoices([])
		} finally {
			setSubmitting(false)
		}
	}

	const handlePageChange = page => {
		applyFiltersAndFetch({
			values: {
				clinic: localFilters.clinic,
				id: localFilters.id,
				tipoComprobante: localFilters.tipoComprobante,
				tipoFactura: localFilters.tipoFactura,
				centroFactura: localFilters.centroFactura,
				numeroComprobante: localFilters.numeroComprobante,
				startDate: localFilters.startDate,
				endDate: localFilters.endDate,
				sentStatus: localFilters.sentStatus
			},
			page
		})
	}

	const handleSort = (filtro, orden) => {
		const nextSort = { filtro, orden }
		setSort(nextSort)
		applyFiltersAndFetch({
			values: {
				clinic: localFilters.clinic,
				institution: localFilters.institution,
				id: localFilters.id,
				tipoComprobante: localFilters.tipoComprobante,
				tipoFactura: localFilters.tipoFactura,
				centroFactura: localFilters.centroFactura,
				numeroComprobante: localFilters.numeroComprobante,
				startDate: localFilters.startDate,
				endDate: localFilters.endDate,
				sentStatus: localFilters.sentStatus
			},
			page: 1,
			sortOverride: nextSort
		})
	}

	const handleDownloadInvoicePackage = async row => {
		const request = {
			centroFactura: row.centroFactura,
			modalidad: row.modalidad,
			idClinica: row.idClinica,
			numeroComprobante: row.numeroComprobante,
			tipoComprobante: row.tipoComprobante,
			tipoFactura: row.tipoFactura,
			idObraSocial: row.idFinanciador,
			cuit: localFilters.cuit,
			fecha: row.fecha,
			origen: row.origen,
			nrsequencia: row.nrsequencia
		}

		const [err, { data } = {}] = await to(generateInvoicePackage(request))
		if (err || !data?.token || !data?.file) {
			addFlashMessage('Ocurrió un error descargando el comprobante')
			return
		}

		const downloadUrl = getDownloadLink(data.token, data.file)
		const link = document.createElement('a')
		link.href = downloadUrl
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	const handleMassAction = async action => {
		showLoader()
		try {
			const sourceInvoices = selectedInvoices
			if (!sourceInvoices.length) {
				addFlashMessage('Debe seleccionar al menos un comprobante')
				return
			}

			const invoicesToProcess =
				action === 'send'
					? sourceInvoices.filter(item => !item.enviado)
					: sourceInvoices.filter(item => item.enviado && !item.descargado)

			if (!invoicesToProcess.length) {
				addFlashMessage('No hay comprobantes para procesar con ese estado')
				return
			}

			const request = mapInvoicesToRequest(invoicesToProcess)
			const [err] = await to(
				action === 'send' ? sendInvoice(request) : cancelInvoiceSending(request)
			)

			if (err) {
				addFlashMessage('Ocurrió un error al procesar la facturación')
				return
			}

			addFlashMessageSuccess(
				action === 'send'
					? 'Todas las facturaciones fueron enviadas con éxito'
					: 'Todas las facturaciones fueron canceladas con éxito'
			)
			handlePageChange(currentPage)
		} finally {
			hideLoader()
		}
	}

	const handleRowAction = async (row, action) => {
		showLoader()
		try {
			const request = mapInvoicesToRequest([row])
			const [err] = await to(
				action === 'send' ? sendInvoice(request) : cancelInvoiceSending(request)
			)

			if (err) {
				addFlashMessage('Ocurrió un error al procesar el comprobante')
				return
			}

			addFlashMessageSuccess(
				action === 'send' ? 'Comprobante enviado correctamente' : 'Envío cancelado correctamente'
			)
			handlePageChange(currentPage)
		} finally {
			hideLoader()
		}
	}

	const handleExportExcel = async () => {
		const request = {
			cuit: localFilters.cuit,
			fechaDesde: localFilters.startDate,
			fechaHasta: localFilters.endDate,
			tipoComprobante: localFilters.tipoComprobante,
			tipoFactura: localFilters.tipoFactura,
			centroFactura: localFilters.centroFactura,
			numeroComprobante: localFilters.numeroComprobante,
			idClinica: localFilters.clinic,
			...(isBillingMode ? { estadoEnvio: localFilters.sentStatus } : {})
		}

		const [err, { data } = {}] = await to(downloadInvoiceList(request))
		if (err) {
			addFlashMessage('Ocurrió un error exportando el listado')
			return
		}

		const blob = new Blob([data])
		const filename = `listado_comprobantes_${new Date().getTime()}.xls`

		if (window.navigator && window.navigator.msSaveOrOpenBlob) {
			window.navigator.msSaveBlob(blob, filename)
		} else {
			const link = document.createElement('a')
			link.href = window.URL.createObjectURL(blob)
			link.download = filename
			link.click()
		}
	}

	const openConfirmModal = (action, row = null) => {
		setConfirmModal({ show: true, action, row, submitting: false })
	}

	const closeConfirmModal = () => {
		setConfirmModal({ show: false, action: null, row: null, submitting: false })
	}

	const handleConfirmActionModal = async () => {
		if (!confirmModal.action) return
		setConfirmModal(prev => ({ ...prev, submitting: true }))
		try {
			if (confirmModal.row) {
				await handleRowAction(confirmModal.row, confirmModal.action)
			} else {
				await handleMassAction(confirmModal.action)
			}
		} finally {
			closeConfirmModal()
		}
	}

	const getConfirmModalTitle = () => {
		const { action, row } = confirmModal
		if (row) {
			return action === 'send'
				? `¿Desea enviar la facturación "${row.factura}"?`
				: `¿Desea cancelar el envío de la facturación "${row.factura}"?`
		}
		return action === 'send'
			? '¿Está seguro de querer enviar todas las facturaciones seleccionadas?'
			: '¿Está seguro de querer cancelar todas las facturaciones seleccionadas?'
	}

	const isInvoiceSelected = invoice =>
		selectedInvoices.some(selected => String(selected.id) === String(invoice.id))

	const toggleInvoiceSelection = invoice => {
		setSelectedInvoices(previous => {
			const exists = previous.some(selected => String(selected.id) === String(invoice.id))
			if (exists) {
				return previous.filter(selected => String(selected.id) !== String(invoice.id))
			}
			return [...previous, invoice]
		})
	}

	const invoiceFields = useMemo(() => {
		if (!isBillingMode) {
			return [
				{ key: 'factura', label: 'N° Factura' },
				{ key: 'fecha', label: 'Fecha' },
				{ key: 'importe', label: 'Importe' },
				{ key: 'acciones', label: 'Acciones' }
			]
		}

		return [
			{ key: 'financiador', label: 'Financiador' },
			{ key: 'factura', label: 'N° Factura' },
			{ key: 'importe', label: 'Importe' },
			{ key: 'descargadaEstado', label: 'Descargada' },
			{ key: 'fecha', label: 'Fecha' },
			{ key: 'acciones', label: 'Acciones' }
		]
	}, [isBillingMode])

	const billingHeaders = useMemo(
		() => [
			{ label: 'Financiador', key: 'financiador', icon: <ArrowDouble /> },
			{ label: 'N° de factura', key: 'factura', icon: <ArrowDouble /> },
			{ label: 'Fecha', key: 'fecha', icon: <ArrowDouble /> },
			{ label: 'Importe', key: 'importe', icon: <ArrowDouble /> },
			{ label: 'Descargada', key: 'descargadaEstado', icon: <ArrowDouble /> },
			{
				label: 'Acciones',
				key: 'acciones',
				render: row => (
					<ActionCell>
						<ActionLink onClick={() => handleDownloadInvoicePackage(row)}>
							<Download color={theme.colors.brand.blueOmint} />
							<Text1
								color={theme.colors.brand.blueOmint}
								size={'14px'}
								style={{ textDecoration: 'underline' }}
							>
								ZIP
							</Text1>
						</ActionLink>
						{!row.enviado && (
							<ActionLink onClick={() => openConfirmModal('send', row)}>
								<Text1
									color={theme.colors.brand.blueOmint}
									size={'14px'}
									style={{ textDecoration: 'underline' }}
								>
									Enviar
								</Text1>
							</ActionLink>
						)}
						{row.enviado && !row.descargado && (
							<ActionLink onClick={() => openConfirmModal('cancel', row)}>
								<Text1
									color={theme.colors.brand.blueOmint}
									size={'14px'}
									style={{ textDecoration: 'underline' }}
								>
									Cancelar
								</Text1>
							</ActionLink>
						)}
					</ActionCell>
				)
			}
		],
		[localFilters.cuit]
	)

	const nonBillingHeaders = useMemo(
		() => [
			{ label: 'N° Factura', key: 'factura', icon: <ArrowDouble /> },
			{ label: 'Fecha', key: 'fecha', icon: <ArrowDouble /> },
			{ label: 'Importe', key: 'importe', icon: <ArrowDouble /> },
			{
				label: 'Acciones',
				key: 'acciones',
				render: row => (
					<ActionCell>
						<ActionLink onClick={() => handleDownloadInvoicePackage(row)}>
							<Download color={theme.colors.brand.blueOmint} />
							<Text1
								color={theme.colors.brand.blueOmint}
								size={'14px'}
								style={{ textDecoration: 'underline' }}
							>
								ZIP
							</Text1>
						</ActionLink>
					</ActionCell>
				)
			}
		],
		[localFilters.cuit]
	)

	return (
		<>
			<div>
				<Section gap="32px">
					<SectionHeader align={'flex-start'} gap="8px">
						<SectionTitle mbDesktop="0px">
							{!isMobile ? 'Facturación' : 'Listado de aranceles'}
						</SectionTitle>
						<Text2 color={theme.colors.text.secondaryText}>
							Buscar comprobantes de facturación
						</Text2>
					</SectionHeader>
					<SectionHeader align={'flex-start'}>
						<Formik
							initialValues={{
								clinic: localFilters.clinic,
								tipoComprobante: localFilters.tipoComprobante,
								tipoFactura: localFilters.tipoFactura,
								centroFactura: localFilters.centroFactura,
								numeroComprobante: localFilters.numeroComprobante,
								startDate: localFilters.startDate,
								endDate: localFilters.endDate,
								institution: localFilters.institution || '',
								id: localFilters.id || '',
								sentStatus: localFilters.sentStatus
							}}
							enableReinitialize
							onSubmit={values => applyFiltersAndFetch({ values, page: 1 })}
						>
							{({ values, handleChange, handleSubmit, setFieldValue }) => (
								<FormFilter as="form" onSubmit={handleSubmit} $wrap={true}>
									<Autocomplete
										width={isMobile ? '100%' : isBillingMode ? billingDesktopFieldWidth : '19%'}
										label="Clínica"
										placeholder="Ver todas las clínicas"
										isClearable
										options={clinicsOptions}
										value={values.clinic}
										onChange={e => setFieldValue('clinic', e.target.value)}
									/>
									{isBillingMode && (
										<Autocomplete
											width={isMobile ? '100%' : billingDesktopFieldWidth}
											name="institution"
											label="Instituciones"
											placeholder="Ver todas las instituciones"
											isClearable
											options={institutionOptions}
											value={values.institution}
											onChange={e => {
												const selectedInstitution = institutionObj.institutions.find(
													institution => String(institution.id) === String(e.target.value)
												)
												setFieldValue('institution', e.target.value)
												setFieldValue('id', selectedInstitution?.id || '')
											}}
										/>
									)}

									<Section
										mb="8px"
										width={isMobile ? '100%' : isBillingMode ? billingDesktopFieldWidth : 'auto'}
										gap="8px"
									>
										<Text2 color={theme.colors.black} lineHeight={'auto'}>Número de factura</Text2>
										<SelectInvoiceNumber>
											<Input
												width={'66px'}
												placeholder="FC"
												name="tipoComprobante"
												value={values.tipoComprobante}
												onChange={handleChange}
											/>
											<Input
												width={'66px'}
												placeholder="A"
												name="tipoFactura"
												value={values.tipoFactura}
												onChange={handleChange}
											/>
											<Input
												width={isMobile ? '100%' : '100px'}
												placeholder="0000"
												name="centroFactura"
												value={values.centroFactura}
												onChange={handleChange}
											/>
											<Input
												width={isMobile ? '100%' : '100px'}
												placeholder="00000000"
												value={values.numeroComprobante}
												onChange={handleChange}
												name="numeroComprobante"
											/>
										</SelectInvoiceNumber>
									</Section>
									<PeriodoDiv>
										<Text2 color={theme.colors.black} lineHeight={'auto'}>Período</Text2>

										<DualDateInput
											width={
												isMobile ? '100%' : isBillingMode ? '100%' : '80%'
											}
											value={{
												startDate: values.startDate,
												endDate: values.endDate
											}}
											onChange={({ startDate, endDate }) => {
												setFieldValue('startDate', startDate)
												setFieldValue('endDate', endDate)
											}}
											renderCalendar={({
												startDate,
												endDate,
												onDatesChange,
												focusedInput,
												onFocusChange
											}) => (
												<DayPickerRangeController
													startDate={startDate}
													endDate={endDate}
													onDatesChange={onDatesChange}
													focusedInput={focusedInput}
													onFocusChange={nextFocusedInput =>
														onFocusChange(nextFocusedInput || 'startDate')
													}
													hideKeyboardShortcutsPanel={true}
													firstDayOfWeek={0}
													minimumNights={0}
													isOutsideRange={date => date.isAfter(moment(), 'day')}
													displayFormat="DD - MM - YYYY"
													numberOfMonths={1}
												/>
											)}
										/>
									</PeriodoDiv>
									{isBillingMode && (
										<Section mb="0px" width={isMobile ? '100%' : billingDesktopFieldWidth}>
											<Text2 color={theme.colors.text.secondaryText}>Estado de factura</Text2>
											<StatusOptions>
												<StatusOption>
													<StatusCheckbox
														type="checkbox"
														checked={values.sentStatus.includes(SENT)}
														onChange={e => {
															const next = e.target.checked
																? [...values.sentStatus, SENT]
																: values.sentStatus.filter(status => status !== SENT)
															setFieldValue('sentStatus', [...new Set(next)])
														}}
													/>
													<Text1 size={'14px'} color={theme.colors.text.primaryText}>
														Facturas enviadas
													</Text1>
												</StatusOption>
												<StatusOption>
													<StatusCheckbox
														type="checkbox"
														checked={values.sentStatus.includes(NOT_SENT)}
														onChange={e => {
															const next = e.target.checked
																? [...values.sentStatus, NOT_SENT]
																: values.sentStatus.filter(status => status !== NOT_SENT)
															setFieldValue('sentStatus', [...new Set(next)])
														}}
													/>
													<Text1 size={'14px'} color={theme.colors.text.primaryText}>
														Facturas no enviadas
													</Text1>
												</StatusOption>
											</StatusOptions>
										</Section>
									)}
									<Button
										width={
											isMobile ? '100%' : isBillingMode ? billingDesktopFieldWidth : '18%'
										}
										color={theme.colors.text.secondaryText}
										disabledColor={theme.colors.greys.disabledGrey}
										$variant={'primary'}
										label={'Consultar'}
										type="submit"
										disabled={submitting}
									/>
								</FormFilter>
							)}
						</Formik>
					</SectionHeader>
					{isBillingMode && (
						<SectionHeader align={'center'} direction="row">
							{!isMobile ? (
								<LinksContainer>
									<ActionTopLink
										onClick={() => openConfirmModal('send')}
										disabled={!selectedInvoices.length}
									>
										<Mail size={14} color={theme.colors.brand.blueOmint} />
										Enviar todo
									</ActionTopLink>
									<Separator>|</Separator>
									<ActionTopLink
										onClick={() => openConfirmModal('cancel')}
										disabled={!selectedInvoices.length}
									>
										<Out size={14} color={theme.colors.brand.blueOmint} />
										Cancelar todo
									</ActionTopLink>
									<Separator>|</Separator>
									<ActionTopLink onClick={handleExportExcel}>
										<Download size={14} color={theme.colors.brand.blueOmint} />
										Exportar excel
									</ActionTopLink>
								</LinksContainer>
							) : (
								<SectionHeader align={'center'} direction="row">
									<LinksContainer>
										<ActionTopLink
											onClick={() => openConfirmModal('send')}
											disabled={!selectedInvoices.length}
										>
											<Mail size={14} color={theme.colors.brand.blueOmint} />
											Enviar todo
										</ActionTopLink>
										<Separator>|</Separator>
										<ActionTopLink
											onClick={() => openConfirmModal('cancel')}
											disabled={!selectedInvoices.length}
										>
											<Out size={14} color={theme.colors.brand.blueOmint} />
											Cancelar todo
										</ActionTopLink>
										<Separator>|</Separator>
										<ActionTopLink onClick={handleExportExcel}>
											<Download size={14} color={theme.colors.brand.blueOmint} />
											Exportar excel
										</ActionTopLink>
									</LinksContainer>
								</SectionHeader>
							)}
						</SectionHeader>
					)}
				</Section>

				<Section>
					{isMobile ? (
						<ResultsList
							results={resultsLst}
							fields={invoiceFields}
							renderField={(field, result) => {
								if (field.key === 'factura') {
									if (isBillingMode) {
										return (
											<InvoiceTopRow key={field.key}>
												<ActionCheckbox
													type="checkbox"
													checked={isInvoiceSelected(result)}
													onChange={() => toggleInvoiceSelection(result)}
												/>
												<Text1 size={'16px'} color={theme.colors.text.primaryText}>
													<strong>{result[field.key]}</strong>
												</Text1>
											</InvoiceTopRow>
										)
									}

									return (
										<Text1 key={field.key} size={'16px'} color={theme.colors.text.primaryText}>
											<strong>{field.label}: </strong>
											{result[field.key]}
										</Text1>
									)
								}

								if (field.key === 'financiador') {
									return (
										<Text1 key={field.key} size={'12px'} color={theme.colors.text.secondaryText}>
											<strong>{field.label}: </strong>
											{result[field.key]}
										</Text1>
									)
								}

								if (field.key === 'importe') {
									return (
										<Text1
											key={field.key}
											size={isBillingMode ? '16px' : '14px'}
											color={theme.colors.text.secondaryText}
											weight={400}
										>
											{result[field.key]}
										</Text1>
									)
								}

								if (field.key === 'descargadaEstado') {
									return (
										<Text1
											key={field.key}
											size={'14px'}
											color={theme.colors.text.secondaryText}
										>
											{result[field.key]}
										</Text1>
									)
								}

								if (field.key === 'fecha') {
									return (
										<Text1
											key={field.key}
											size={'12px'}
											color={theme.colors.text.secondaryText}
										>
											{result[field.key]}
										</Text1>
									)
								}

								if (field.key === 'acciones') {
									return (
										<ActionCell key={field.key}>
											<ActionLink onClick={() => handleDownloadInvoicePackage(result)}>
												<Download color={theme.colors.brand.blueOmint} />
												<Text1
													color={theme.colors.brand.blueOmint}
													size={'14px'}
													style={{ textDecoration: 'underline', cursor: 'pointer' }}
												>
													ZIP
												</Text1>
											</ActionLink>
											{isBillingMode && !result.enviado && (
												<ActionLink onClick={() => openConfirmModal('send', result)}>
													<Text1
														color={theme.colors.brand.blueOmint}
														size={'14px'}
														style={{ textDecoration: 'underline', cursor: 'pointer' }}
													>
														Enviar
													</Text1>
												</ActionLink>
											)}
											{isBillingMode && result.enviado && !result.descargado && (
												<ActionLink onClick={() => openConfirmModal('cancel', result)}>
													<Text1
														color={theme.colors.brand.blueOmint}
														size={'14px'}
														style={{ textDecoration: 'underline', cursor: 'pointer' }}
													>
														Cancelar
													</Text1>
												</ActionLink>
											)}
										</ActionCell>
									)
								}

								return null
							}}
						/>
					) : (
						<ResultsListDesk
							handleSort={handleSort}
							results={resultsLst}
							isSearch={true}
							headers={isBillingMode ? billingHeaders : nonBillingHeaders}
							useCheckboxes={isBillingMode}
							getSelectedItems={setSelectedInvoices}
						/>
					)}
					{resultsLst.length > 0 && (
						<Pagination
							totalPages={totalPages}
							currentPage={currentPage}
							onPageChange={handlePageChange}
						/>
					)}
				</Section>
			</div>
			{confirmModal.show && (
				<Modal isFullHeight={isMobile}>
					<ModalCard>
						<div>
							<IconWrap>
								<Info
									color={
										confirmModal.action === 'cancel'
											? theme.colors.feedback.error
											: theme.colors.brand.lightBlue
									}
								/>
							</IconWrap>
							<ModalTitle>{getConfirmModalTitle()}</ModalTitle>
						</div>
						<ModalActions>
							<Button
								label={confirmModal.action === 'cancel' ? 'Cancelar envío' : 'Aceptar'}
								$variant={'primary'}
								size={isMobile ? 'mobile' : 'xlarge'}
								onClick={handleConfirmActionModal}
								disabled={confirmModal.submitting}
							/>
							<Button
								label={'Cancelar'}
								$variant={'tertiary'}
								size={isMobile ? 'mobile' : 'xlarge'}
								onClick={closeConfirmModal}
								disabled={confirmModal.submitting}
							/>
						</ModalActions>
					</ModalCard>
				</Modal>
			)}
		</>
	)
}

const mapStateToProps = ({ auth }) => ({ auth })

const mapDispatchToProps = {
	showLoader,
	hideLoader,
	addFlashMessage,
	addFlashMessageSuccess
}

export default connect(mapStateToProps, mapDispatchToProps)(Invoice)

const ActionCell = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`

const ActionLink = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	padding: 0;
	display: inline-flex;
	align-items: center;
	gap: 4px;
`

const ActionSelection = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 6px;
`

const ActionCheckbox = styled.input`
	accent-color: ${props => props.theme.colors.brand.blueOmint};
`

const InvoiceTopRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

const StatusOptions = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	margin-top: 8px;
	flex-wrap: nowrap;

	@media (max-width: 768px) {
		flex-wrap: wrap;
	}
`

const StatusOption = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	white-space: nowrap;
`

const StatusCheckbox = styled.input`
	accent-color: ${props => props.theme.colors.brand.blueOmint};
`

const LinksContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
`

const ActionTopLink = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	color: ${props => props.theme.colors.brand.blueOmint};
	font-size: 16px;
	font-weight: 600;
	padding: 0;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	text-decoration: underline;

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

const Separator = styled.span`
	color: ${props => props.theme.colors.brand.blueOmint};
	font-size: 16px;
`

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1200;
	background: rgba(0, 0, 0, 0.35);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
`

const ModalCard = styled.div`
	width: 100%;
	max-width: 640px;
	background: ${props => props.theme.colors.brand.white};
	border-radius: 20px;
	padding: 48px 32px 28px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		height: 100%;
		border-radius: 20px;
		justify-content: space-between;
		padding: 48px 16px 24px;
	}
`

const IconWrap = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`

const ModalTitle = styled(Text1)`
	text-align: center;
	font-size: 24px;
	color: ${props => props.theme.colors.text.primaryText};
`

const ModalActions = styled.div`
	width: 50%;
	display: flex;
	flex-direction: column;
	gap: 8px;
`
